import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom'; // Keep useSearchParams for internal use

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalendarPopup({
  selectedDate, onDateSelect, onClose, anchorRef, minSelectableDate, maxSelectableDate,
}) {
  const popupRef = useRef(null);
  const today = useRef(new Date());

  const [displayMonth, setDisplayMonth] = useState(today.current.getMonth());
  const [displayYear, setDisplayYear] = useState(today.current.getFullYear());
  const [popupStyle, setPopupStyle] = useState({});
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [yearPickerStart, setYearPickerStart] = useState(
    Math.floor(today.current.getFullYear() / 16) * 16,
  );

  const updatePopupPosition = useCallback(() => {
    if (anchorRef.current && popupRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popupHeight = popupRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      let newTop;
      const offset = 8;

      if (spaceBelow >= popupHeight + offset) {
        newTop = anchorRect.bottom + offset;
      } else if (spaceAbove >= popupHeight + offset) {
        newTop = anchorRect.top - popupHeight - offset;
      } else {
        newTop = anchorRect.bottom + offset;
        if (newTop + popupHeight > viewportHeight) {
          newTop = viewportHeight - popupHeight - offset;
          if (newTop < 0) newTop = offset;
        }
      }

      setPopupStyle({
        top: `${newTop}px`,
        left: `${anchorRect.left}px`,
        position: 'fixed',
        zIndex: 1000,
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)
        && anchorRef.current && !anchorRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  useEffect(() => {
    const initialPositionTimeout = setTimeout(updatePopupPosition, 0);

    const handleScrollAndResize = () => {
      updatePopupPosition();
    };

    window.addEventListener('scroll', handleScrollAndResize, true);
    window.addEventListener('resize', handleScrollAndResize);
    return () => {
      clearTimeout(initialPositionTimeout);
      window.removeEventListener('scroll', handleScrollAndResize, true);
      window.removeEventListener('resize', handleScrollAndResize);
    };
  }, [updatePopupPosition]);

  const daysInMonth = getDaysInMonth(displayYear, displayMonth);
  const firstDay = getFirstDayOfMonth(displayYear, displayMonth);
  const calendarDays = [];

  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  const isDateDisabled = (year, month, day) => {
    const dateToCheck = new Date(year, month, day);
    dateToCheck.setHours(0, 0, 0, 0);

    if (maxSelectableDate) {
      if (isNaN(maxSelectableDate.getTime())) return false; // Treat invalid maxSelectableDate as no restriction
      const maxDate = new Date(maxSelectableDate);
      maxDate.setHours(0, 0, 0, 0);
      if (dateToCheck.getTime() > maxDate.getTime()) return true;
    }
    if (minSelectableDate) {
      if (isNaN(minSelectableDate.getTime())) return false; // Treat invalid minSelectableDate as no restriction
      const minDate = new Date(minSelectableDate);
      minDate.setHours(0, 0, 0, 0);
      if (dateToCheck.getTime() < minDate.getTime()) return true;
    }

    const currentToday = new Date();
    currentToday.setHours(0, 0, 0, 0);
    return dateToCheck.getTime() > currentToday.getTime();
  };

  const handleDayClick = (day) => {
    if (day && !isDateDisabled(displayYear, displayMonth, day)) {
      const newDate = new Date(displayYear, displayMonth, day);
      onDateSelect(newDate);
      onClose();
    }
  };

  const goToPreviousMonth = () => {
    setDisplayMonth((prevMonth) => {
      if (prevMonth === 0) {
        setDisplayYear((prevYear) => prevYear - 1);
        return 11;
      }
      return prevMonth - 1;
    });
  };

  const goToNextMonth = () => {
    setDisplayMonth((prevMonth) => {
      if (prevMonth === 11) {
        setDisplayYear((prevYear) => prevYear + 1);
        return 0;
      }
      return prevMonth + 1;
    });
  };

  const handleYearSelect = (year) => {
    setDisplayYear(year);
    setDisplayMonth(0);
    setShowYearPicker(false);
  };

  const goToPreviousYearsBlock = () => {
    setYearPickerStart((prevStart) => prevStart - 16);
  };

  const goToNextYearsBlock = () => {
    setYearPickerStart((prevStart) => prevStart + 16);
  };

  const yearsToShow = 16;

  const isNextMonthDisabled = (() => {
    const nextMonth = new Date(displayYear, displayMonth + 1, 1);
    if (maxSelectableDate) {
      if (isNaN(maxSelectableDate.getTime())) return false;
      const maxDate = new Date(maxSelectableDate);
      maxDate.setDate(1);
      return nextMonth.getTime() > maxDate.getTime();
    }
    const currentToday = new Date();
    currentToday.setDate(1);
    return nextMonth.getTime() > currentToday.getTime();
  })();

  const isNextYearsBlockDisabled = (() => {
    const endOfBlockYear = yearPickerStart + yearsToShow - 1;
    if (maxSelectableDate) {
      if (isNaN(maxSelectableDate.getTime())) return false;
      const maxYear = new Date(maxSelectableDate).getFullYear();
      return endOfBlockYear >= maxYear;
    }
    return endOfBlockYear >= today.current.getFullYear();
  })();

  return (
    <div
      ref={popupRef}
      style={popupStyle}
      className="bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-72 h-[310px]"
    >
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={showYearPicker ? goToPreviousYearsBlock : goToPreviousMonth}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <span
          className="font-semibold text-lg text-gray-800 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded-md flex items-center"
          onClick={() => setShowYearPicker(!showYearPicker)}
        >
          {showYearPicker ? `${yearPickerStart} - ${yearPickerStart + yearsToShow - 1}` : `${monthNames[displayMonth]} ${displayYear}`}
          <svg className="h-4 w-4 ml-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
        <button
          onClick={showYearPicker ? goToNextYearsBlock : goToNextMonth}
          className={`p-2 rounded-full hover:bg-gray-100 ${(showYearPicker && isNextYearsBlockDisabled) || (!showYearPicker && isNextMonthDisabled)
            ? 'cursor-not-allowed pointer-events-none'
            : ''
          }`}
          disabled={(showYearPicker && isNextYearsBlockDisabled) || (!showYearPicker && isNextMonthDisabled)}
        >
          <svg
            className={`h-5 w-5 ${(showYearPicker && isNextYearsBlockDisabled) || (!showYearPicker && isNextMonthDisabled)
              ? 'text-gray-400'
              : 'text-gray-700'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {showYearPicker ? (
        <div className="grid grid-cols-4 gap-1 text-center">
          {[...Array(yearsToShow)].map((_, index) => {
            const year = yearPickerStart + index;
            const isDisabled = (() => {
              if (maxSelectableDate) {
                if (isNaN(maxSelectableDate.getTime())) return false;
                return year > new Date(maxSelectableDate).getFullYear();
              }
              return year > today.current.getFullYear();
            })();

            return (
              <button
                key={year}
                onClick={() => handleYearSelect(year)}
                className={`w-full py-2 rounded-md text-sm font-medium
                  ${year === displayYear ? 'bg-blue-500 text-white' : 'text-gray-800 hover:bg-blue-100'}
                  ${isDisabled ? 'text-gray-400 bg-gray-100 cursor-not-allowed pointer-events-none' : ''}
                `}
                disabled={isDisabled}
              >
                {year}
              </button>
            );
          })}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 text-center text-sm font-medium text-gray-500 mb-2">
            {dayNamesShort.map((day, index) => (
              <div key={index} className="py-1">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {calendarDays.map((day, index) => {
              const date = new Date(displayYear, displayMonth, day);
              const isDisabled = day && isDateDisabled(displayYear, displayMonth, day);
              const isToday = day && date.toDateString() === today.current.toDateString();

              return (
                <button
                  key={index}
                  onClick={() => handleDayClick(day)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-sm
                    ${day === null ? 'cursor-default' : 'hover:bg-blue-100'}
                    ${selectedDate && day && !isNaN(selectedDate.getTime()) && new Date(selectedDate).toDateString() === new Date(displayYear, displayMonth, day).toDateString()
                    ? 'bg-blue-500 text-white'
                    : isToday && !isDisabled
                      ? 'bg-blue-200 text-blue-800 font-bold'
                      : day && !isDisabled
                        ? 'text-gray-800'
                        : day
                          ? 'text-gray-400 bg-gray-100 cursor-not-allowed pointer-events-none'
                          : 'opacity-0'
                    }
                  `}
                  disabled={!day || isDisabled}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Removed searchParams and setSearchParams from props, now uses useSearchParams internally
function CreatedDateFilter() {
  const [searchParams, setSearchParams] = useSearchParams(); // Get search params internally

  const formatInternalToDisplay = (internalDateString) => {
    if (!internalDateString) return '';
    const [year, month, day] = internalDateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const parseDisplayToInternal = (displayDateString) => {
    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = displayDateString.match(regex);

    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const year = parseInt(match[3], 10);

      const date = new Date(year, month, day);

      if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);

        if (date.getTime() <= today.getTime()) {
          return `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        }
      }
    }
    return null;
  };

  const createdFromParam = searchParams.get('createdFrom');
  const createdToParam = searchParams.get('createdTo');

  const createdFromDateObj = createdFromParam ? new Date(createdFromParam) : null;
  const createdToDateObj = createdToParam ? new Date(createdToParam) : null;

  const validCreatedFromDate = (createdFromDateObj && !isNaN(createdFromDateObj.getTime())) ? createdFromDateObj : null;
  const validCreatedToDate = (createdToDateObj && !isNaN(createdToDateObj.getTime())) ? createdToDateObj : null;

  const [fromDisplayValue, setFromDisplayValue] = useState(validCreatedFromDate ? formatInternalToDisplay(createdFromParam) : '');
  const [toDisplayValue, setToDisplayValue] = useState(validCreatedToDate ? formatInternalToDisplay(createdToParam) : '');

  const [isFromFocused, setIsFromFocused] = useState(false);
  const [isToFocused, setIsToFocused] = useState(false);

  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  const handleFocus = (setIsFocusedFn) => {
    setIsFocusedFn(true);
  };

  const handleBlur = (setIsFocusedFn) => {
    setIsFocusedFn(false);
  };

  const handleInputChange = useCallback((e, setterDisplayValue, paramName, currentDisplayValue) => {
    const input = e.target;
    const previousValue = currentDisplayValue;
    let newCursorPos = input.selectionStart;

    const rawValue = e.target.value;
    const cleanRawValue = rawValue.replace(/\D/g, '');

    let maskedValue = '';
    let cleanIndex = 0;

    for (let i = 0; i < 10; i++) {
      if (i === 2 || i === 5) {
        if (cleanIndex < cleanRawValue.length) {
          maskedValue += '-';
        }
      } else if (cleanIndex < cleanRawValue.length) {
        maskedValue += cleanRawValue[cleanIndex];
        cleanIndex++;
      } else {
        break;
      }
    }

    if (maskedValue.length > 10) {
      maskedValue = maskedValue.substring(0, 10);
    }

    const previousNonDigitsBeforeCursor = (previousValue.substring(0, input.selectionStart).match(/-/g) || []).length;
    const currentNonDigitsBeforeCursor = (maskedValue.substring(0, newCursorPos).match(/-/g) || []).length;
    newCursorPos += (currentNonDigitsBeforeCursor - previousNonDigitsBeforeCursor);

    if (previousValue.length < maskedValue.length && (newCursorPos === 3 || newCursorPos === 6)) {
      newCursorPos++;
    } else if (previousValue.length > maskedValue.length && (newCursorPos === 3 || newCursorPos === 6)) {
      newCursorPos--;
    }

    newCursorPos = Math.max(0, Math.min(newCursorPos, maskedValue.length));

    setterDisplayValue(maskedValue);

    setTimeout(() => {
      if (input) {
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    if (maskedValue.length === 10) {
      const parsedInternal = parseDisplayToInternal(maskedValue);
      setSearchParams((params) => {
        if (parsedInternal) {
          params.set(paramName, parsedInternal);
        } else {
          params.delete(paramName);
        }
        return params;
      }, { replace: true });
    } else {
      setSearchParams((params) => {
        params.delete(paramName);
        return params;
      }, { replace: true });
    }
  }, [setSearchParams, parseDisplayToInternal]);

  useEffect(() => {
    const formattedFrom = validCreatedFromDate ? formatInternalToDisplay(createdFromParam) : '';
    if (!isFromFocused && formattedFrom !== fromDisplayValue) {
      setFromDisplayValue(formattedFrom);
    }
  }, [createdFromParam, isFromFocused, fromDisplayValue, formatInternalToDisplay, validCreatedFromDate]);

  useEffect(() => {
    const formattedTo = validCreatedToDate ? formatInternalToDisplay(createdToParam) : '';
    if (!isToFocused && formattedTo !== toDisplayValue) {
      setToDisplayValue(formattedTo);
    }
  }, [createdToParam, isToFocused, toDisplayValue, formatInternalToDisplay, validCreatedToDate]);

  useEffect(() => {
    if (showFromCalendar || showToCalendar) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showFromCalendar, showToCalendar]);

  const handleFromDateSelect = (date) => {
    const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    setSearchParams((params) => {
      params.set('createdFrom', formattedDate);
      return params;
    }, { replace: true });
    setFromDisplayValue(formatInternalToDisplay(formattedDate));
    setShowFromCalendar(false);
  };

  const handleToDateSelect = (date) => {
    const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    setSearchParams((params) => {
      params.set('createdTo', formattedDate);
      return params;
    }, { replace: true });
    setToDisplayValue(formatInternalToDisplay(formattedDate));
    setShowToCalendar(false);
  };

  const shouldFromLabelFloat = isFromFocused || !!fromDisplayValue;
  const shouldToLabelFloat = isToFocused || !!toDisplayValue;

  return (
    <div className="w-64 -ml-4">
      <div className="text-sm font-medium text-gray-800 -mt-2 mb-2">
        Created Date
      </div>
      <div className="flex flex-col w-full mt-6">
        <div className="relative mb-4">
          <div className="relative border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 h-11">
            <label
              htmlFor="from-date"
              className={`absolute left-3 transition-all duration-200 ease-in-out px-1
                  ${shouldFromLabelFloat
                ? '-top-2 text-xs text-gray-600 bg-white z-10'
                : 'top-1/2 -translate-y-1/2 text-base text-gray-600'}`}
            >
              From
            </label>
            <input
              id="from-date"
              type="text"
              ref={fromInputRef}
              value={fromDisplayValue}
              placeholder={shouldFromLabelFloat ? 'dd-mm-yyyy' : ''}
              onFocus={() => handleFocus(setIsFromFocused)}
              onBlur={() => handleBlur(setIsFromFocused)}
              onChange={(e) => handleInputChange(e, setFromDisplayValue, 'createdFrom', fromDisplayValue)}
              className="w-full p-3 bg-transparent text-base pr-10 outline-none cursor-text"
            />
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              onClick={() => setShowFromCalendar(true)}
            >
              <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                <rect x="7" y="12" width="2" height="2" fill="currentColor" />
              </svg>
            </div>
          </div>
          {showFromCalendar && (
            ReactDOM.createPortal(
              <CalendarPopup
                selectedDate={validCreatedFromDate}
                onDateSelect={handleFromDateSelect}
                onClose={() => setShowFromCalendar(false)}
                anchorRef={fromInputRef}
                maxSelectableDate={validCreatedToDate || new Date()}
              />,
              document.body,
            )
          )}
        </div>

        <div className="relative mb-4">
          <div className="relative border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 h-11">
            <label
              htmlFor="to-date"
              className={`absolute left-3 transition-all duration-200 ease-in-out px-1
                  ${shouldToLabelFloat
                ? '-top-2 text-xs text-gray-600 bg-white z-10'
                : 'top-1/2 -translate-y-1/2 text-base text-gray-600'}`}
            >
              To
            </label>
            <input
              id="to-date"
              type="text"
              ref={toInputRef}
              value={toDisplayValue}
              placeholder={shouldToLabelFloat ? 'dd-mm-yyyy' : ''}
              onFocus={() => handleFocus(setIsToFocused)}
              onBlur={() => handleBlur(setIsToFocused)}
              onChange={(e) => handleInputChange(e, setToDisplayValue, 'createdTo', toDisplayValue)}
              className="w-full p-3 bg-transparent text-base pr-10 outline-none cursor-text"
            />
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              onClick={() => setShowToCalendar(true)}
            >
              <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                <rect x="7" y="12" width="2" height="2" fill="currentColor" />
              </svg>
            </div>
          </div>
          {showToCalendar && (
            ReactDOM.createPortal(
              <CalendarPopup
                selectedDate={validCreatedToDate}
                onDateSelect={handleToDateSelect}
                onClose={() => setShowToCalendar(false)}
                anchorRef={toInputRef}
                minSelectableDate={validCreatedFromDate}
                maxSelectableDate={new Date()}
              />,
              document.body,
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default CreatedDateFilter;
